import { Fragment, FunctionComponent, ReactNode, useState } from 'react'
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Collapse, Divider, Paper, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material'
import { CompareArrowsOutlined, ErrorOutline, ExpandLess, ExpandMore, GavelOutlined, Launch, ScheduleOutlined } from '@mui/icons-material'
import { Link } from 'react-router-dom'
import moment from 'moment'
import { IReadinessFinding, ReadinessCategory, ReadinessSeverity } from '../../../shared/services/readiness/readiness.model'
import { READINESS_SEVERITY_CHIP_COLORS, READINESS_SEVERITY_DESCRIPTIONS, READINESS_SEVERITY_LABELS, READINESS_SEVERITY_ORDER, ReadinessSeverityChipColor } from './readiness-labels'
import { TooltipHover } from '../miscellaneous-components/tooltip-hover'

const CATEGORY_ICONS: Record<ReadinessCategory, JSX.Element> = {
  completeness: <ErrorOutline />,
  consistency: <CompareArrowsOutlined />,
  compliance: <GavelOutlined />,
  governance: <ScheduleOutlined />,
}

// Severity palette: READINESS_SEVERITY_CHIP_COLORS in readiness-labels (shared with the partner
// console, so one place decides what Critical looks like).
type SeverityChipColor = ReadinessSeverityChipColor
const severityChipColor = (severity: ReadinessSeverity): SeverityChipColor => READINESS_SEVERITY_CHIP_COLORS[severity]

// A partner reading a client's findings cannot follow the fix link: the deep link is relative to
// the client's own account and would navigate the partner into their own organisation.
const READ_ONLY_FIX_HINT = 'Open the client account to fix this'

const formatEvidenceValue = (value: string | number | string[] | null): string => {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'number') return value.toLocaleString('en-GB', { maximumFractionDigits: 2 })
  return String(value)
}

// Sentence-case labels from the evidence keys ('monthsWithData' reads 'Months with data').
const formatEvidenceKey = (key: string): string => {
  const words = key.replace(/([A-Z])/g, ' $1').toLowerCase().trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

const FindingRow: FunctionComponent<{
  finding: IReadinessFinding,
  isAcknowledged: boolean,
  canAcknowledge: boolean,
  canCreateTask: boolean,
  onAcknowledge: (finding: IReadinessFinding) => void,
  onRelease: (finding: IReadinessFinding) => void,
  onCreateTask?: (finding: IReadinessFinding) => void,
  renderExtraActions?: (finding: IReadinessFinding) => ReactNode,
  readOnly: boolean,
}> = ({ finding, isAcknowledged, canAcknowledge, canCreateTask, onAcknowledge, onRelease, onCreateTask, renderExtraActions, readOnly }) => {
  const [expanded, setExpanded] = useState(false)
  const evidenceEntries = Object.entries(finding.evidence || {})
  const acknowledgement = finding.acknowledgement
  const readOnlyFixLabel = (
    <TooltipHover text={READ_ONLY_FIX_HINT} placement='top' backgroundColor='common.white'>
      <Typography variant='body2' sx={{ color: 'text.secondary', whiteSpace: 'nowrap', cursor: 'default' }} onClick={(e) => e.stopPropagation()}>
        {finding.fixLabel}
      </Typography>
    </TooltipHover>
  )

  return (
    <Box>
      <Stack
        direction='row'
        alignItems='center'
        spacing={2}
        onClick={() => setExpanded(!expanded)}
        sx={{ px: 2.5, py: 1.5, cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
      >
        <Box sx={{ color: 'text.secondary', display: 'flex' }} title={finding.category}>
          {CATEGORY_ICONS[finding.category] || <ErrorOutline />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant='body1' sx={{ fontWeight: 500 }}>{finding.title}</Typography>
          {finding.entity && <Typography variant='body2' sx={{ color: 'text.secondary' }}>{finding.entity.name}</Typography>}
        </Box>
        {finding.impact && finding.impact.tco2e != null &&
          <Typography variant='body2' sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            <b>{finding.impact.tco2e.toLocaleString('en-GB', { maximumFractionDigits: 1 })} tCO2e</b>
            {finding.impact.pctOfTotal != null && ` (${finding.impact.pctOfTotal}%)`}
          </Typography>}
        {!isAcknowledged && readOnly && readOnlyFixLabel}
        {!isAcknowledged && !readOnly &&
          <Button
            variant='outlined'
            size='small'
            component={Link}
            to={finding.deepLink}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant='body2'>{finding.fixLabel}</Typography>
          </Button>}
        {expanded ? <ExpandLess sx={{ color: 'text.secondary' }} /> : <ExpandMore sx={{ color: 'text.secondary' }} />}
      </Stack>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: 2.5, pb: 2.5, pl: 8 }}>
          <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: '640px' }}>{finding.detail}</Typography>
          {evidenceEntries.length > 0 &&
            <Paper variant='outlined' sx={{ mt: 1.5, maxWidth: '480px' }}>
              <Typography variant='caption' sx={{ color: 'text.secondary', px: 1.75, pt: 1, display: 'block' }}>The numbers behind this finding</Typography>
              <Table size='small'>
                <TableBody>
                  {evidenceEntries.map(([key, value]) => (
                    <TableRow key={key} sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell>{formatEvidenceKey(key)}</TableCell>
                      <TableCell align='right' sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatEvidenceValue(value)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>}
          {isAcknowledged && acknowledgement &&
            <Box sx={{ mt: 1.5, p: 1.5, backgroundColor: 'secondary.light', borderRadius: '5px', maxWidth: '640px' }}>
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                <b>Acknowledged</b>{acknowledgement.createdByUserName ? ` by ${acknowledgement.createdByUserName}` : ''} on {moment(acknowledgement.createdAt).format('DD/MM/YYYY')}
                {acknowledgement.expiresAt ? `, review by ${moment(acknowledgement.expiresAt).format('DD/MM/YYYY')}` : ''}: "{acknowledgement.reason}". Disclosed in your generated reports and audit pack.
              </Typography>
            </Box>}
          <Stack direction='row' spacing={1.5} sx={{ mt: 2 }} alignItems='center'>
            {!isAcknowledged && <>
              {readOnly
                ? readOnlyFixLabel
                : <Button variant='contained' size='small' component={Link} to={finding.deepLink} disableElevation startIcon={<Launch fontSize='small' />}>
                  <Typography variant='body2' sx={{ pl: 0.5 }}>{finding.fixLabel}</Typography>
                </Button>}
              {canAcknowledge &&
                <Button variant='text' size='small' onClick={() => onAcknowledge(finding)}>
                  <Typography variant='body2'>Acknowledge</Typography>
                </Button>}
              {canCreateTask && onCreateTask &&
                <Button variant='text' size='small' onClick={() => onCreateTask(finding)}>
                  <Typography variant='body2'>Create task</Typography>
                </Button>}
              {renderExtraActions && renderExtraActions(finding)}
            </>}
            {isAcknowledged && canAcknowledge &&
              <Button variant='text' size='small' onClick={() => onRelease(finding)}>
                <Typography variant='body2'>Remove acknowledgement</Typography>
              </Button>}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  )
}

// The derived findings, grouped by severity (open) plus the acknowledged group. Findings can
// never be marked complete: they clear when the data changes, or are acknowledged with a
// disclosed reason.
export const ReadinessChecksList: FunctionComponent<{
  findings: IReadinessFinding[],
  acknowledged: IReadinessFinding[],
  view: 'open' | 'acknowledged' | 'all',
  canAcknowledge: boolean,
  /** Defaults to canAcknowledge (the readiness page's original coupling). The ESOS checks list
   * has no acknowledgements but still creates tasks, so it splits the two. */
  canCreateTask?: boolean,
  onAcknowledge: (finding: IReadinessFinding) => void,
  onRelease: (finding: IReadinessFinding) => void,
  onCreateTask?: (finding: IReadinessFinding) => void,
  /** Extra per-finding actions rendered after the built-in buttons (e.g. Add estimate). */
  renderExtraActions?: (finding: IReadinessFinding) => ReactNode,
  /** Read-only mode (the partner console): fix labels render as text with a hint instead of
   * router links, because the deep links are relative to the client's own account. */
  readOnly?: boolean,
}> = ({ findings, acknowledged, view, canAcknowledge, canCreateTask, onAcknowledge, onRelease, onCreateTask, renderExtraActions, readOnly = false }) => {

  const groups: { key: string, label: string, description: string, chipColor: SeverityChipColor | 'success', rows: IReadinessFinding[], isAcknowledged: boolean, defaultExpanded: boolean }[] = []
  if (view !== 'acknowledged') {
    for (const severity of READINESS_SEVERITY_ORDER) {
      const rows = findings.filter(f => f.severity === severity)
      if (rows.length === 0) continue
      groups.push({
        key: severity,
        label: READINESS_SEVERITY_LABELS[severity],
        description: READINESS_SEVERITY_DESCRIPTIONS[severity],
        chipColor: severityChipColor(severity),
        rows,
        isAcknowledged: false,
        defaultExpanded: severity === 'blocks_reporting',
      })
    }
  }
  if (view !== 'open' && acknowledged.length > 0) {
    groups.push({
      key: 'acknowledged',
      label: 'Acknowledged',
      description: 'explained and disclosed in your generated reports and audit pack',
      chipColor: 'success',
      rows: acknowledged,
      isAcknowledged: true,
      defaultExpanded: view === 'acknowledged',
    })
  }

  return (
    <>
      {groups.map(group => (
        <Paper key={group.key} variant='outlined' sx={{ mt: 2, borderRadius: '10px', overflow: 'hidden' }}>
          <Accordion sx={{ boxShadow: 0 }} defaultExpanded={group.defaultExpanded} disableGutters>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 2.5 }}>
              <Stack direction='row' alignItems='center' spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                <Chip
                  label={group.label}
                  color={group.chipColor === 'ink' ? undefined : group.chipColor}
                  variant='outlined'
                  size='small'
                  sx={group.chipColor === 'ink' ? { borderColor: 'text.primary', color: 'text.primary' } : undefined}
                />
                <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>{group.rows.length}</Typography>
                <Typography variant='body2' sx={{ color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{group.description}</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0, borderTop: '1px solid', borderColor: 'lightGrey.main' }}>
              {group.rows.map((finding, index) => (
                <Fragment key={`${finding.checkId}|${finding.entityKey}`}>
                  <FindingRow
                    finding={finding}
                    isAcknowledged={group.isAcknowledged}
                    canAcknowledge={canAcknowledge}
                    canCreateTask={canCreateTask ?? canAcknowledge}
                    onAcknowledge={onAcknowledge}
                    onRelease={onRelease}
                    onCreateTask={onCreateTask}
                    renderExtraActions={renderExtraActions}
                    readOnly={readOnly}
                  />
                  {index !== group.rows.length - 1 && <Divider />}
                </Fragment>
              ))}
            </AccordionDetails>
          </Accordion>
        </Paper>
      ))}
    </>
  )
}
